import { useEffect, useMemo, useState } from 'react';
import { ActionIcon, Button, Group, Paper, Skeleton, Stack, Table, Text, TextInput, Title } from '@mantine/core';
import { useDebounceValue } from 'usehooks-ts';
import { Icon, LoadMorePagination } from '@/components';
import { churchAdminAccessService } from '@/services/church-admin-access.service';
import { useChurchAdminAccess, useRemoveChurchAdminAccessMutation } from '@/hooks';
import type { ChurchAdminAccessItem } from '@/types';
import { ChurchAdminAccessModal } from '@/pages/ChurchAdmins/ChurchAdminAccessModal';
import styles from './ChurchAdmins.module.sass';

const LAYOUT = {
  LIMIT: 10,
};

const getUserDisplayName = (item: ChurchAdminAccessItem) => {
  const fullName = `${item.firstName || ''} ${item.lastName || ''}`.trim();
  return fullName || item.email || 'N/A';
};

export const ChurchAdmins = () => {
  const [termInput, setTermInput] = useState<string>('');
  const [debouncedTerm] = useDebounceValue(termInput, 400);
  const [extraItems, setExtraItems] = useState<ChurchAdminAccessItem[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [lastPageSize, setLastPageSize] = useState<number>(0);
  const [expandedChurchRows, setExpandedChurchRows] = useState<Record<string, boolean>>({});
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedAdmin, setSelectedAdmin] = useState<ChurchAdminAccessItem | null>(null);
  const [isRemovingKey, setIsRemovingKey] = useState<string>('');

  const { data, isLoading } = useChurchAdminAccess({
    limit: LAYOUT.LIMIT,
    term: debouncedTerm || undefined,
  });
  const removeChurchMutation = useRemoveChurchAdminAccessMutation();

  const firstPageItems = useMemo(
    () => (data?.items ?? []).filter((item) => (item.churches ?? []).length > 0),
    [data?.items]
  );
  const total = data?.total;
  const items = useMemo(() => [...firstPageItems, ...extraItems], [firstPageItems, extraItems]);

  useEffect(() => {
    setExtraItems([]);
    setExpandedChurchRows({});
    setLastPageSize(data?.items?.length ?? 0);
    if (firstPageItems.length > 0) {
      setCursor(firstPageItems[firstPageItems.length - 1].userId);
      return;
    }
    setCursor(undefined);
  }, [data?.items, firstPageItems]);

  const hasMore = useMemo(() => {
    if (typeof total === 'number') {
      return items.length < total;
    }
    return lastPageSize === LAYOUT.LIMIT;
  }, [items.length, lastPageSize, total]);

  const handleLoadMore = async () => {
    if (!hasMore || isLoadingMore || !cursor) return;

    setIsLoadingMore(true);
    try {
      const response = await churchAdminAccessService.list({
        limit: LAYOUT.LIMIT,
        cursor,
        term: debouncedTerm || undefined,
      });
      const responseItems = (response.items ?? []).filter((item) => (item.churches ?? []).length > 0);
      setExtraItems((prev) => [...prev, ...responseItems]);
      setLastPageSize(response.items.length);

      if (responseItems.length > 0) {
        setCursor(responseItems[responseItems.length - 1].userId);
      }
    } catch (error) {
      console.error('Failed to load more church admins', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleToggleChurches = (userId: string) => {
    setExpandedChurchRows((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const handleOpenModal = (admin: ChurchAdminAccessItem | null = null) => {
    setSelectedAdmin(admin);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedAdmin(null);
  };

  const handleRemoveChurch = async ({ userId, churchId }: { userId: string; churchId: string }) => {
    const row = items.find((item) => item.userId === userId);
    if (!row?.userId) return;

    const removeKey = `${userId}-${churchId}`;
    setIsRemovingKey(removeKey);
    try {
      await removeChurchMutation.mutateAsync({ churchId, userId: row.userId });
      setExtraItems((prev) =>
        prev
          .map((item) =>
            item.userId !== userId
              ? item
              : { ...item, churches: item.churches.filter((church) => church.id !== churchId) }
          )
          .filter((item) => item.churches.length > 0)
      );
    } finally {
      setIsRemovingKey('');
    }
  };

  const skeletonRows = Array.from({ length: LAYOUT.LIMIT }).map((_, index) => (
    <Table.Tr key={`skeleton-${index}`}>
      <Table.Td>
        <Skeleton height={28} radius="sm" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={28} radius="sm" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={28} radius="sm" />
      </Table.Td>
      <Table.Td>
        <Skeleton height={28} radius="sm" />
      </Table.Td>
    </Table.Tr>
  ));

  return (
    <Stack px="xxl" py="md" gap="md">
      <Group justify="space-between">
        <TextInput
          placeholder="Search by name or email"
          value={termInput}
          onChange={(e) => setTermInput(e.currentTarget.value)}
          leftSection={<Icon name="IconSearch" size={16} />}
          w={360}
        />
        <Button leftSection={<Icon name="IconPlus" />} onClick={() => handleOpenModal()}>
          Assign Churches
        </Button>
      </Group>

      <Paper radius="md" p="xl">
        <Title order={3} mb="md">
          Church Admins
        </Title>
        <Table className={styles.table} striped highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th className={styles.userColumn}>User</Table.Th>
              <Table.Th className={styles.emailColumn}>Email</Table.Th>
              <Table.Th className={styles.churchColumn}>Churches</Table.Th>
              <Table.Th className={styles.actionsColumn}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading ? skeletonRows : null}
            {!isLoading &&
              items.map((item) => {
                const isExpanded = Boolean(expandedChurchRows[item.userId]);
                const visibleChurches = isExpanded ? item.churches : item.churches.slice(0, 1);
                return (
                  <Table.Tr key={item.userId}>
                    <Table.Td className={styles.userColumn}>{getUserDisplayName(item)}</Table.Td>
                    <Table.Td className={styles.emailColumn}>{item.email || 'N/A'}</Table.Td>
                    <Table.Td className={styles.churchColumn}>
                      <Stack gap={4}>
                        {visibleChurches.map((church) => {
                          const removeKey = `${item.userId}-${church.id}`;
                          return (
                            <Group key={church.id} className={styles.churchRow} gap="xs" wrap="nowrap">
                              <Text className={styles.churchName}>
                                {church.name}
                                {church.zipCode ? ` (${church.zipCode})` : ''}
                              </Text>
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                aria-label="Remove church access"
                                onClick={() => handleRemoveChurch({ userId: item.userId, churchId: church.id })}
                                loading={isRemovingKey === removeKey}
                              >
                                <Icon name="IconTrash" size={14} />
                              </ActionIcon>
                            </Group>
                          );
                        })}
                        {item.churches.length > 1 ? (
                          <Button
                            variant="subtle"
                            size="compact-xs"
                            className={styles.toggleChurchesButton}
                            onClick={() => handleToggleChurches(item.userId)}
                          >
                            {isExpanded ? 'Hide all' : 'Show all'}
                          </Button>
                        ) : null}
                      </Stack>
                    </Table.Td>
                    <Table.Td className={styles.actionsColumn}>
                      <Button size="xs" variant="default" onClick={() => handleOpenModal(item)}>
                        Add Churches
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            {!isLoading && items.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <Text c="dimmed" ta="center">
                    No church admins found
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : null}
          </Table.Tbody>
        </Table>
      </Paper>

      <LoadMorePagination
        loaded={items.length}
        total={total ?? items.length}
        hasMore={hasMore}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        onLoadMore={handleLoadMore}
      />

      <ChurchAdminAccessModal opened={isModalOpen} selectedAdmin={selectedAdmin} onClose={handleCloseModal} />
    </Stack>
  );
};
